import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { PostalAddressForm } from '#src/components/settings/PostalAddressForm/index.tsx';
import { ActiveUserAvatar } from '#src/components/user/ActiveUserAvatar/index.tsx';
import { MyClubLink } from '#src/components/user/MyClubLink/index.tsx';
import { UserName } from '#src/components/user/UserName/index.tsx';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { getCommonCardShardsCount } from '#src/lib/currentUser/index.tsx';

import type {
  CurrentUseProvider_ethereumAccounts,
  CurrentUseProvider_fiatAccounts,
  CurrentUseProvider_sportProfile,
  CurrentUserProvider_anyCard,
  CurrentUserProvider_availableBalances,
  CurrentUserProvider_cardCounts,
  CurrentUserProvider_completablePromotionalEventCompletion,
  CurrentUserProvider_conversionCreditsWithoutPurchase,
  CurrentUserProvider_currentSportUnreadNotificationsCount,
  CurrentUserProvider_currentUser,
  CurrentUserProvider_inGameCurrencyBalance,
  CurrentUserProvider_myCart,
  CurrentUserProvider_sportConversionCredits,
  CurrentUserProvider_sportConversionCreditsWithoutPurchase,
  CurrentUserProvider_userSportProfile,
  CurrentUserProvider_walletRecovery,
  commonCardMarketplace_currentUser,
  onCurrentUserWasUpdated,
  onCurrentUserWasUpdatedVariables,
  onDeviceWasUpdated,
  onDeviceWasUpdatedVariables,
} from './__generated__/queries.graphql.ts';

const userSportProfileFragment = gql`
  fragment CurrentUserProvider_userSportProfile on UserSportProfileInterface {
    id
    onboarded
    clubName
    shieldUrl
    clubBadge {
      id
      pictureUrl
      name
    }
    achievementBadges {
      id
    }
  }
` as TypedDocumentNode<CurrentUserProvider_userSportProfile>;

export const cardCountsFragment = gql`
  fragment CurrentUserProvider_cardCounts on CurrentUser {
    slug
    cardCounts(sport: $sport) {
      total
      limited
      rare
      super_rare: superRare
      superRare
      unique
      common
      custom_series: customSeries
      customSeries
    }
  }
` as TypedDocumentNode<CurrentUserProvider_cardCounts>;

export const sportProfile = gql`
  fragment CurrentUseProvider_sportProfile on CurrentUser {
    slug
    anyUserSportProfile(sport: $sport) {
      id
      commonGameOnboarded
      proUnlocked
      ...CurrentUserProvider_userSportProfile
    }
    ...CurrentUserProvider_cardCounts
  }
  ${userSportProfileFragment}
  ${cardCountsFragment}
` as TypedDocumentNode<CurrentUseProvider_sportProfile>;

export const walletRecovery = gql`
  fragment CurrentUserProvider_walletRecovery on CurrentUser {
    slug
    wallet {
      id
      ethereumAddress
      solanaAddress
      status
      holdsValue
      recoveryOptions {
        id
        destination
        method
        status
      }
    }
  }
` as TypedDocumentNode<CurrentUserProvider_walletRecovery>;

export const ethereumAccounts = gql`
  fragment CurrentUseProvider_ethereumAccounts on CurrentUser {
    slug
    myAccounts {
      id
      account {
        id
        sorareManaged
        accountable {
          ... on Node {
            id
          }
          ... on EthereumAccount {
            id
            address
          }
          ... on StarkwareAccount {
            id
          }
        }
      }
    }
  }
` as TypedDocumentNode<CurrentUseProvider_ethereumAccounts>;
export const fiatAccounts = gql`
  fragment CurrentUseProvider_fiatAccounts on CurrentUser {
    slug
    myAccounts {
      id
      accountable {
        ... on Node {
          id
        }
        ... on PrivateFiatWalletAccount {
          id
          state
          firstName
          lastName
          nationalityCode
          dob
          countryOfResidenceCode
          kycStatus
          kycRefusedReason
          depositBankAccount {
            ... on BankAccountInterface {
              id
            }
            ... on IbanBankAccount {
              id
              ownerName
              bic
              iban
            }
          }
          publicInfo {
            id
            currency
          }
        }
      }
    }
  }
` as TypedDocumentNode<CurrentUseProvider_fiatAccounts>;

export const myCartCardFragment = gql`
  fragment CurrentUserProvider_anyCard on AnyCardInterface {
    slug
    sport
    rarityTyped
    anyPlayer {
      slug
    }
    anyTeam {
      slug
    }
    seasonYear
    collectionIncreaseScoreBreakdownAfterTransfer
      @include(if: $withCollectionPredictions) {
      total
    }
  }
` as TypedDocumentNode<CurrentUserProvider_anyCard>;

export const myCartFragment = gql`
  fragment CurrentUserProvider_myCart on CurrentUser {
    slug
    myCart(sport: $sport) {
      id
      sport
      mainCartItems {
        id
        card {
          slug
          ...CurrentUserProvider_anyCard
        }
        state
        price {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      savedForLaterCartItems {
        id
        card {
          slug
          ...CurrentUserProvider_anyCard
        }
        state
        savedForLater
        price {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      affectedCardCollections {
        id
        scoreIncrease
        bonusIncrease
      }
      price {
        ...useMonetaryAmount_monetaryAmount
      }
      activePromotionalEvents {
        slug
        endDate
        startDate
        sport
        eligibilityRules {
          id
          eligibleCollections {
            cartCardsCount
            cardCollections {
              slug
              rarity
              anyTeam {
                slug
              }
              sportSeason {
                name
              }
            }
          }
          eligibleCartCardsCount
        }
      }
    }
  }
  ${useMonetaryAmount.fragments.monetaryAmount}
  ${myCartCardFragment}
` as TypedDocumentNode<CurrentUserProvider_myCart>;

const conversionCreditsWithoutPurchase = gql`
  fragment CurrentUserProvider_conversionCreditsWithoutPurchase on ConversionCredit {
    id
    endDate
    maxDiscount {
      ...useMonetaryAmount_monetaryAmount
    }
    totalDiscount {
      ...useMonetaryAmount_monetaryAmount
    }
    percentageDiscount
    singleUse
    settlementDelay
    status
    sport
  }
  ${useMonetaryAmount.fragments.monetaryAmount}
` as TypedDocumentNode<CurrentUserProvider_conversionCreditsWithoutPurchase>;

export const currentUserConversionCredits = gql`
  fragment CurrentUserProvider_sportConversionCredits on CurrentUser {
    slug
    sportConversionCredits(first: 500, includeUsing: true) {
      nodes {
        id
        ...CurrentUserProvider_conversionCreditsWithoutPurchase
        purchase {
          ... on TokenAuction {
            id
            anyCards {
              slug
              sport
            }
          }
          ... on TokenPrimaryOffer {
            id
            anyCards {
              slug
              sport
            }
          }
        }
      }
    }
  }
  ${conversionCreditsWithoutPurchase}
` as TypedDocumentNode<CurrentUserProvider_sportConversionCredits>;

const currentUserConversionCreditsWithoutPurchase = gql`
  fragment CurrentUserProvider_sportConversionCreditsWithoutPurchase on CurrentUser {
    slug
    sportConversionCredits(first: 500, includeUsing: true) {
      nodes {
        id
        ...CurrentUserProvider_conversionCreditsWithoutPurchase
      }
    }
  }
  ${conversionCreditsWithoutPurchase}
` as TypedDocumentNode<CurrentUserProvider_sportConversionCreditsWithoutPurchase>;

const completablePromotionalEventCompletion = gql`
  fragment CurrentUserProvider_completablePromotionalEventCompletion on CurrentUser {
    slug
    completablePromotionalEvent {
      slug
      sport
      promotionalEventCompletion {
        id
        aasmState
        actualAmounts {
          ...useMonetaryAmount_monetaryAmount
        }
      }
    }
  }
  ${useMonetaryAmount.fragments.monetaryAmount}
` as TypedDocumentNode<CurrentUserProvider_completablePromotionalEventCompletion>;

export const currentSportUnreadNotificationsCountFragment = gql`
  fragment CurrentUserProvider_currentSportUnreadNotificationsCount on CurrentUser {
    slug
    # this is required for Notifications refetch to work
    lastUnreadNotificationCreatedAt
    currentSportUnreadNotificationsCount: unreadNotificationsCount(
      sports: [$sport]
      categories: $notificationCategories
    )
    currentSportImportantNotificationsCount: unreadNotificationsCount(
      sports: [$sport]
      categories: $importantNotificationCategories
    )
  }
` as TypedDocumentNode<CurrentUserProvider_currentSportUnreadNotificationsCount>;

export const commonCardMarketplaceFragment = gql`
  fragment commonCardMarketplace_currentUser on CurrentUser {
    slug
    cardCounts(sport: $sport) {
      common
    }
    gemsBalances: inGameCurrencyBalances(
      sport: $sport
      inGameCurrencies: [COMMON_GEM]
    ) {
      amount
    }
  }
` as TypedDocumentNode<commonCardMarketplace_currentUser>;

const inGameCurrencyBalanceFragment = gql`
  fragment CurrentUserProvider_inGameCurrencyBalance on CurrentUser {
    slug
    cardShardsChests(
      sport: $sport
      rarities: [common, limited, rare, super_rare, unique]
    ) {
      id
      rarity
      cardShardsCount
      cardSet {
        slug
        name
      }
    }
    xpBalances: inGameCurrencyBalances(
      sport: $sport
      inGameCurrencies: [
        COMMON_XP
        LIMITED_XP
        RARE_XP
        SUPER_RARE_XP
        UNIQUE_XP
      ]
    ) {
      amount
      currency
      capAlmostReached
    }
    energyBalance: inGameCurrencyBalances(
      sport: $sport
      inGameCurrencies: [
        LIMITED_ENERGY
        RARE_ENERGY
        SUPER_RARE_ENERGY
        UNIQUE_ENERGY
      ]
    ) {
      amount
      currency
      capAlmostReached
    }

    wheelTickets: inGameCurrencyBalances(
      sport: $sport
      inGameCurrencies: [WHEEL_TICKET, COMMON_WHEEL_TICKET]
    ) {
      amount
      currency
    }
    goldenWheelTickets: inGameCurrencyBalances(
      sport: $sport
      inGameCurrencies: [GOLDEN_WHEEL_TICKET, COMMON_GOLDEN_WHEEL_TICKET]
    ) {
      amount
      currency
    }
    craftClues: inGameCurrencyBalances(sport: $sport) {
      amount
      currency
      cap
    }
    anyUserSportProfile(sport: $sport) {
      id
      blueprintCardsCount
    }
    ...getCommonCardShardsCount_currentUser
  }
  ${getCommonCardShardsCount.fragments.currentUser}
` as TypedDocumentNode<CurrentUserProvider_inGameCurrencyBalance>;

export const availableBalancesFragment = gql`
  fragment CurrentUserProvider_availableBalances on CurrentUser {
    slug
    availableBalances {
      wei {
        ...useMonetaryAmount_monetaryAmount
      }
      lamport {
        ...useMonetaryAmount_monetaryAmount
      }
      eurCents {
        ...useMonetaryAmount_monetaryAmount
      }
      usdCents {
        ...useMonetaryAmount_monetaryAmount
      }
      gbpCents {
        ...useMonetaryAmount_monetaryAmount
      }
    }
  }
  ${useMonetaryAmount.fragments.monetaryAmount}
` as TypedDocumentNode<CurrentUserProvider_availableBalances>;

export const currentUser = gql`
  fragment CurrentUserProvider_currentUser on CurrentUser {
    id
    slug
    suspended
    forcedIntoOnboarding(sport: $sport)
    footballLast30DaysLineupsCount
    createdAt
    email
    nickname
    active
    fromPath
    blockedUntil
    confirmedDevice
    hasPassword
    # this is required for Notifications refetch to work
    lastUnreadNotificationCreatedAt
    referrer {
      slug
      suspended
      nickname
      createdAt
      profile {
        id
        verified
      }
      ...ActiveUserAvatar_user
      ...UserName_publicUserInfoInterface
    }
    unclaimedReferralRewardsCount
    referralRewardsCount
    referralUrl
    myUnclaimedTasksCount(sport: $sport)

    sorarePrivateKey {
      iv
      salt
      encryptedPrivateKey
    }
    sorareAddressBalance
    starkKey
    depositedEth
    currentDevice {
      id
    }
    availableBalanceForWithdrawal
    otpRequiredForLogin
    featureFlagCustomAttributes
    phoneNumber
    phoneNumberVerificationRequested
    phoneNumberVerified
    confirmed
    profile {
      id
      verified
      status
      discordUsername
      twitterUsername
      enabledWallets
    }
    unverifiedPhoneNumber
    userSettings {
      id
      disableAllEmails
      locale
      currency
      fiatCurrency
      cryptocurrency
      lifecycle
      hideBalance
      tcuStatus
      rewardCurrency
      discoverable
      billingAddress {
        streetAddress
        additionalAddress
        zipcode
        city
        countryCode
        region
        country {
          slug
          name
        }
      }
      ...PostalAddressForm_userSettings
    }
    rampSignedUrl
    rampSupported
    moonpaySupported
    apiKey
    mustAcceptTcus
    followingCount
    followersCount
    connectedOauths {
      id
      email
      provider
    }
    mangopayUser {
      id
      blocked
      requiredDocuments
      scaEnrollmentState
      additionalKycRequests {
        id
        requestType
        aasmState
      }
    }
    noCardRouteEnabled
    so5NoCardRouteOpened
    warningFlags {
      id
      reason
      relatedUsers {
        slug
        nickname
        ...MyClubLink_publicUserInfoInterface
      }
      until
    }
    completablePromotionalEvent {
      slug
      startDate
      endDate
      name
      description
      marketBannerTitle
      promotionDetailsRewardDescription
      promotionDetailsRewardPictureUrl
      blogUrl
      pictureUrl
      eligibilityRules {
        id
        minimumDepositAmount
      }
      probableRewardConfigs {
        id
        rewardConfigs {
          id
          ... on ShopItemRewardConfig {
            id
            shopItem {
              id
              pictureUrl
            }
          }
          ... on ProbabilisticBundleRewardConfig {
            id
            probabilisticBundleConfig {
              id
              slots {
                probableConfigs {
                  id
                  conversionCredit {
                    id
                    maxDiscount {
                      ...useMonetaryAmount_monetaryAmount
                    }
                    percentageDiscount
                  }
                }
              }
            }
          }
        }
      }
      completionRules
      completionAmount {
        ...useMonetaryAmount_monetaryAmount
      }
      promotionalEventCompletion {
        id
        aasmState
        expiresAt
        actualAmounts {
          ...useMonetaryAmount_monetaryAmount
        }
        targetAmount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ... on DepositPromotionalEvent {
        slug
        completedParticipants
      }
    }
    loan {
      id
    }
    daily_tasks: tasks(sport: $sport, periodicity: DAILY) {
      id
      aasmState
    }
    weekly_tasks: tasks(sport: $sport, periodicity: WEEKLY) {
      id
      aasmState
    }
    game_weekly_tasks: tasks(sport: $sport, periodicity: GAME_WEEKLY) {
      id
      aasmState
    }
    unclaimedSo5Rewards(sport: $sport) {
      slug
      so5Fixture {
        slug
        endDate
      }
    }
    passkeys {
      id
      externalId
      publicKey
      sorarePrivateKey {
        iv
        salt
        encryptedPrivateKey
      }
    }
    loyaltyBoxes: unopenedProbabilisticBundles(sport: $sport, design: LOYALTY) {
      nodes {
        id
        openableAt
        isOpenable
        config {
          id
          name
          pictureUrl
        }
      }
    }
    unclaimedDeliverableItemOrders(sport: $sport) {
      id
      aasmState
      expiresAt
      deliverableItem {
        slug
        id
        name
        pictureUrl
        mandatoryWearableSizeInput
        ... on JerseyDeliverableItem {
          slug
          signed
          competition {
            slug
            name
            pictureUrl
          }
          team: anyTeam {
            slug
            name
            pictureUrl
          }
        }
        ... on WearableDeliverableItem {
          slug
          signed
        }
      }
    }
    claimablePacks(sport: $sport) {
      id
      cards {
        slug
      }
    }
    shouldMigrateEth
    shouldMigrateEthBeforeWithdrawal
    ...CurrentUserProvider_sportConversionCredits
    ...CurrentUseProvider_sportProfile
    ...CurrentUserProvider_inGameCurrencyBalance
    ...CurrentUserProvider_walletRecovery
    ...CurrentUseProvider_ethereumAccounts
    ...CurrentUseProvider_fiatAccounts
    ...ActiveUserAvatar_user
    ...CurrentUserProvider_myCart
    ...commonCardMarketplace_currentUser
    ...CurrentUserProvider_completablePromotionalEventCompletion
    ...CurrentUserProvider_availableBalances
    ...CurrentUserProvider_currentSportUnreadNotificationsCount
  }
  ${walletRecovery}
  ${sportProfile}
  ${ethereumAccounts}
  ${fiatAccounts}
  ${ActiveUserAvatar.fragments.user}
  ${PostalAddressForm.fragments.userSettings}
  ${UserName.fragments.user}
  ${myCartFragment}
  ${commonCardMarketplaceFragment}
  ${MyClubLink.fragments.user}
  ${completablePromotionalEventCompletion}
  ${availableBalancesFragment}
  ${useMonetaryAmount.fragments.monetaryAmount}
  ${inGameCurrencyBalanceFragment}
  ${currentUserConversionCredits}
  ${currentSportUnreadNotificationsCountFragment}
` as TypedDocumentNode<CurrentUserProvider_currentUser>;

export const onDeviceSubscription = gql`
  subscription onDeviceWasUpdated {
    deviceWasUpdated {
      id
      eventType
    }
  }
` as TypedDocumentNode<onDeviceWasUpdated, onDeviceWasUpdatedVariables>;

export const subscription = gql`
  subscription onCurrentUserWasUpdated(
    $sport: Sport!
    $notificationCategories: [NotificationCategoryInput!]!
    $importantNotificationCategories: [NotificationCategoryInput!]!
  ) {
    currentUserWasUpdated {
      slug
      availableBalanceForWithdrawal
      sorareAddressBalance
      confirmed
      shouldMigrateEth
      shouldMigrateEthBeforeWithdrawal
      mangopayUser {
        id
        blocked
      }
      pendingDeposits {
        id
        date
        providerType
        transactionHash
        amounts {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      involvedLiveTokenAuctions(first: 10) {
        nodes {
          id
          privateCurrentPrice
          privateMinNextBid
          currency
          bestBid {
            id
            bidder: userBidder {
              slug
            }
            amounts {
              ...useMonetaryAmount_monetaryAmount
            }
          }
        }
      }
      myUnclaimedTasksCount(sport: $sport)
      ...CurrentUserProvider_sportConversionCreditsWithoutPurchase
      ...CurrentUseProvider_fiatAccounts
      ...CurrentUserProvider_inGameCurrencyBalance
      ...CurrentUserProvider_currentSportUnreadNotificationsCount
      ...CurrentUserProvider_completablePromotionalEventCompletion
      ...CurrentUserProvider_cardCounts
      ...commonCardMarketplace_currentUser
      ...CurrentUserProvider_availableBalances
    }
  }
  ${useMonetaryAmount.fragments.monetaryAmount}
  ${currentUserConversionCreditsWithoutPurchase}
  ${fiatAccounts}
  ${currentSportUnreadNotificationsCountFragment}
  ${completablePromotionalEventCompletion}
  ${commonCardMarketplaceFragment}
  ${inGameCurrencyBalanceFragment}
  ${cardCountsFragment}
  ${availableBalancesFragment}
` as TypedDocumentNode<
  onCurrentUserWasUpdated,
  onCurrentUserWasUpdatedVariables
>;
